import { SetServiceDependencies } from '../../infra/startup/types';
import { base64Decode, base64Encode } from '../../utils/base64Encoder';

import bindAllMethods from '../../utils/bindAllMethods';
import {
  _getPostLoginRedirect,
  PostLoginRedirectType
} from './getPostLoginRedirect';
import { INavigationService } from '../navigationService';
import {
  CreatePostRedirectParamType,
  GetStatesParamOptionsType,
  PostRedirectType,
  StateParamObejectType,
  StatesParamType
} from './types';
import { InternalService } from '../../core/services';

export default class StateParamHandlerService extends InternalService {
  // @TODO: This should use the constants file
  private postRedirectAblePaths = ['/loggedin', '/loggedout'];
  public postRedirectKeys = {
    postLoginRedirect: 'postLoginRedirect',
    postLogoutRedirect: 'postLogoutRedirect'
  };

  private _navigationService: INavigationService;

  constructor() {
    super();
    bindAllMethods(this);
  }

  static getPostLoginRedirect(
    state: PostLoginRedirectType,
    basePath?: string
  ): string {
    return _getPostLoginRedirect(state, basePath);
  }

  setDependencies(options: SetServiceDependencies): void {
    const { services } = options;
    this._navigationService = services?.navigationService;
    this._markAsReady();
  }

  createLoginStateObject(postLoginRedirect?: string): StatesParamType {
    const stateSearchKey = 'state';
    const inviteSearchKey = 'invite';
    const stateValue = 'shell';
    const excludeKeys = [stateSearchKey, inviteSearchKey];
    const isUserInvite =
      new URLSearchParams(this._navigationService.location.search).get(
        inviteSearchKey
      ) === 'true';

    const postRedirect: PostRedirectType = postLoginRedirect && {
      key: this.postRedirectKeys.postLoginRedirect,
      value: postLoginRedirect
    };

    const includeParams = isUserInvite && {
      [inviteSearchKey]: isUserInvite.toString()
    };

    const stateObject = this.createStateParamObject({
      postRedirect,
      includeParams: {
        ...includeParams,
        state: stateValue
      },
      excludeKeys: excludeKeys
    });

    return {
      state: this.encodeStateParam(stateObject)
    };
  }

  createLogoutStateObject(
    postLogoutRedirect?: string,
    email?: string
  ): StatesParamType {
    const stateValue = 'shell';

    const postRedirect: PostRedirectType = postLogoutRedirect && {
      key: this.postRedirectKeys.postLogoutRedirect,
      value: postLogoutRedirect
    };

    const includeParams: Record<string, string> = {
      state: stateValue
    };

    if (email) {
      includeParams.email = email;
    }

    const stateObject = this.createStateParamObject({
      postRedirect,
      includeParams
    });

    return {
      ...stateObject,
      state: this.encodeStateParam(stateObject)
    };
  }

  createStateParamObject(
    params?: CreatePostRedirectParamType
  ): StateParamObejectType {
    const { postRedirect, excludeKeys = [], includeParams = {} } = params || {};
    const { key, value } = postRedirect || {};

    const stateObject = {};

    if (key && value) stateObject[key] = value;

    const searchParams = new URLSearchParams(
      this._navigationService.location.search
    );
    searchParams?.forEach((paramValue, paramKey) => {
      if (!excludeKeys.find((key) => key === paramKey)) {
        stateObject[paramKey] = paramValue;
      }
    });

    return {
      ...stateObject,
      ...includeParams
    };
  }

  encodeStateParam(stateParamObject: StateParamObejectType): string {
    return base64Encode(stateParamObject);
  }

  static decodeStateParam(stateParam: string): StateParamObejectType {
    return base64Decode(stateParam);
  }

  isPostRedirectable(): boolean {
    return this.isValidPath() && this.hasPostRedirectkey();
  }

  isValidPath(): boolean {
    const currentPath = this._navigationService.location.pathname;

    return this.postRedirectAblePaths.some((validPath) =>
      currentPath.includes(validPath)
    );
  }

  hasPostRedirectkey(): boolean {
    const stateParamDecoded = this._getStateParam({ decode: true });
    const stateParamsObject = new URLSearchParams(stateParamDecoded);
    return Object.keys(this.postRedirectKeys).some((value) => {
      return stateParamsObject?.get(value);
    });
  }

  handleRedirect(): void {
    const stateParamDecoded = this._getStateParam({ decode: true });
    const stateParamsObject = new URLSearchParams(stateParamDecoded);

    const postRedirect = stateParamsObject?.get(
      this.postRedirectKeys.postLogoutRedirect
    );
    const email = stateParamsObject?.get('email');

    if (this.isValidPath() && postRedirect) {
      let redirectUrl = postRedirect;

      if (email) {
        const separator = postRedirect.includes('?') ? '&' : '?';
        redirectUrl = `${postRedirect}${separator}email=${encodeURIComponent(
          email
        )}`;
      }

      this.redirect(redirectUrl);
    }
  }

  private _getStateParam(options?: GetStatesParamOptionsType) {
    const { decode } = options || {};

    const searchParams = new URLSearchParams(
      this._navigationService?.location?.search
    );
    const stateParamEncoded = searchParams?.get('state');
    return decode
      ? stateParamEncoded
        ? StateParamHandlerService.decodeStateParam(stateParamEncoded)
        : ''
      : stateParamEncoded;
  }

  private redirect(postRedirect: string): void {
    // TODO maybe we can create a infinite promise to blocking the runtime inside the navigation service, but currently the redirect is sync, need to check the changes in commons
    this._navigationService.redirect(postRedirect);
  }
}
